import { AttributeModel, SparePartOverviewModel } from "../../api-clients/product-api-v1"
import { FH_EMPTY_VALUE, sparePartsTableHeaderCodes } from "../constants/constants"
import { attributes, sparePartsAttributes } from "../constants/search-constants"
import { EnrichedSparePartArticleModel, SparePartCellDataType, TileSparePart } from "../types/global"
import { SparePartTableHeader } from "../types/product"
import { FhSparePart } from "../types/search"

export function getSparePartTileAttributes(fhSparePart: FhSparePart): TileSparePart {
  const name = fhSparePart.attribute?.find((attr) => attr.name === attributes.NAME)
  const id = fhSparePart.attribute?.find((attr) => attr.name === attributes.PRODUCT_ID)
  const image = fhSparePart.attribute?.find((attr) => attr.name === attributes.IMAGE)
  const category = fhSparePart.attribute?.find((attr) => attr.name === attributes.SYSTEMS_CATEGORY)
  const sparePartTable = fhSparePart.attribute?.find((attr) => attr.name === attributes.SPT_DETAILS)
  const articlesCount = fhSparePart.attribute?.find((attr) => attr.name === attributes.ARTICLES_COUNT)
  const yearManufactureFrom = fhSparePart.attribute?.find((attr) => attr.name === attributes.YEAR_MANUFACTURE_FROM)
  const yearManufactureUntil = fhSparePart.attribute?.find((attr) => attr.name === attributes.YEAR_MANUFACTURE_UNTIL)

  return {
    name: name?.value?.[0]?.value ?? "",
    category: category?.value?.[0]?.value ?? "",
    id: id?.value?.[0]?.value ?? "",
    sparePartsTable: sparePartTable?.value?.[0]?.value ? JSON.parse(sparePartTable?.value?.[0]?.value) : null,
    image: image?.value?.[0]?.value ?? "",
    articlesCount: +articlesCount?.value?.[0]?.value ?? null, // Comes as a string from Fredhopper, cast it to number or set it to null if it's missing
    yearManufactureFrom: yearManufactureFrom?.value?.[0]?.value ?? "",
    yearManufactureUntil: yearManufactureUntil?.value?.[0]?.value ?? "",
  }
}

/* There are some special cases for values coming from Fredhopper
  one of which is "empty_value" that needs to be handled by this function */
export const specialFhValue = (value: string) => {
  return value === null || value === undefined || value === FH_EMPTY_VALUE ? "" : value
}

const getTableDetails = (
  attributesList: AttributeModel[],
  sparePartArticlesList: EnrichedSparePartArticleModel[],
  columnHeaders
) => {
  const rows = sparePartArticlesList.map((item) => {
    const attributeData = !!item?.attributeValues?.length
      ? item.attributeValues.map((attribute) => {
          return { [attribute.key]: attribute.rawValue }
        })
      : []

    const extraColumns = Object.assign({}, ...attributeData)
    const { SPARE_PART_POSITION, ARTICLE_NUMBER, DESCRIPTION, REPLACEMENT_FOR, REPLACED_BY, NOTES, ASSETS } =
      sparePartsTableHeaderCodes

    const hasAssets = !!item?.documents?.length || !!item.videos?.length

    return {
      id: item?.idInternal ?? item?.key,
      type: item?.type,
      available: item?.state?.available ?? true,
      documents: item?.documents ?? null,
      videos: item?.videos ?? null,
      data: {
        [SPARE_PART_POSITION]: item?.position ?? null, //#
        [ARTICLE_NUMBER]: item?.type !== sparePartsAttributes.sptType ? item?.key : null, //article number
        [ASSETS]: hasAssets ? "Media" : null,
        [DESCRIPTION]: item?.name ?? null, //description
        [REPLACEMENT_FOR]: item?.state?.replacementFor ?? null, //replacement For
        [REPLACED_BY]: item?.state?.replacedBy ?? null, //replaced By
        [NOTES]: item?.remarks ?? null, //notes
        ...extraColumns,
      },
    }
  })

  const extraHeaders = attributesList.map((extraHeader: SparePartTableHeader) => {
    return {
      key: extraHeader.key,
      value: extraHeader.name,
    }
  })

  columnHeaders = [...columnHeaders.slice(0, 2), ...extraHeaders, ...columnHeaders.slice(2)]

  const usedTableHeaders = rows?.flatMap((row: SparePartCellDataType) => {
    return Object.keys(row.data).filter((item) => !!row.data[item])
  })

  const uniqieTableHeaders = Array.from(new Set(usedTableHeaders))
  const columns = columnHeaders.filter((col: { key: string; value: string }) => uniqieTableHeaders.includes(col.key))
  return { columns, rows }
}

export const formatProductData = (data: SparePartOverviewModel, columnHeaders) => {
  const sparePartTableDetails = JSON.stringify(
    getTableDetails(
      data?.sparePartArticles?.attributesList,
      data?.sparePartArticles?.sparePartArticlesList,
      columnHeaders
    ) ?? {}
  )

  return {
    name: data?.name,
    productid: data?.key,
    articlenumber: data?.key,
    _imageurl: data?.image?.large,
    year_manufacture_from: data?.manufactureFrom,
    year_manufacture_until: data?.manufactureTo,
    systems_display_category: "",
    secondid: "",
    spare_part_table_details: sparePartTableDetails,
    main_articles_count: data?.sparePartArticles?.sparePartArticlesList?.length,
  }
}
